import React, { useMemo, useState } from 'react';
import {
  Heading1,
  Heading4,
  Card,
  SecondaryCTAButton,
  Stack,
  Strong,
  InfoNotification,
} from '@ovotech/element-v4';
import { CancelMoveModal } from '../CancelMoveModal/CancelMoveModal';
import { ChangeMovingDateModal } from '../ChangeMovingDateModal/ChangeMovingDateModal';
import { DateFormat } from '@/src/types/MoveOut';
import { AnalyticsClick } from '@ovotech/ui-tools';
import {
  HOME_MOVES_CHANGE_MOVING_DATE,
  HOME_MOVES_CANCEL_MOVE,
} from '@/src/constants/analytics-subjects';
import styles from './MovingHomePendingCard.module.css';
import { isBefore, startOfDay } from 'date-fns';
import { isCotMoveInTooFarInTheFuture } from '../../utils/cotMoveIn';

type MovingHomePendingCardProps = {
  address?: string;
  initialMovingDate: string;
  isTakingOvo: boolean;
  newAddressIsCot: boolean;
};

export const MovingHomePendingCard = ({
  address,
  initialMovingDate,
  isTakingOvo,
  newAddressIsCot,
}: MovingHomePendingCardProps) => {
  const [showCancelMoveModal, setShowCancelMoveModal] = useState(false);
  const [showChangeMovingDateModal, setShowChangeMovingDateModal] =
    useState(false);

  const [movingDate, setMovingDate] = useState(new Date(initialMovingDate));

  const movingDateText = useMemo(() => {
    const date = new Date(movingDate);

    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  }, [movingDate]);

  const handleChangeMovingDate = (movingDate: DateFormat) => {
    const { year, month, day } = movingDate;
    const fullYear = parseInt(year, 10);
    const monthNum = parseInt(month, 10);
    const dayNum = parseInt(day, 10);

    // Use Date constructor with numbers instead of string parsing
    const date = new Date(fullYear, monthNum - 1, dayNum); // month is 0-indexed

    setShowChangeMovingDateModal(false);
    setMovingDate(date);
  };

  const isMovingDateInPast = useMemo(() => {
    const date = new Date(movingDate);
    return isBefore(date, startOfDay(new Date()));
  }, [movingDate]);

  const showComeBackLaterCard =
    !!address &&
    isCotMoveInTooFarInTheFuture(newAddressIsCot, movingDate) &&
    newAddressIsCot;

  return (
    <Card>
      {showCancelMoveModal && (
        <CancelMoveModal onClose={() => setShowCancelMoveModal(false)} />
      )}

      {showChangeMovingDateModal && (
        <ChangeMovingDateModal
          onMoveOutUpdated={handleChangeMovingDate}
          onClose={() => setShowChangeMovingDateModal(false)}
          initialMovingDate={movingDate}
        />
      )}

      <Heading1 className={styles.heading}>
        {isTakingOvo ? (
          <>Thanks for taking OVO with you</>
        ) : (
          <>You've told us you're moving home</>
        )}
      </Heading1>

      <div className={styles.description}>
        {isTakingOvo ? (
          <>
            We'll start powering your new home on{' '}
            <Strong>{movingDateText}</Strong>. Don't worry if your account isn't
            updated yet, it can take a while to process a move, so keep checking
            back.
          </>
        ) : (
          <>
            Thank you for telling us about your{' '}
            {isMovingDateInPast ? 'recent' : 'upcoming'} move.
          </>
        )}
      </div>

      {address ? (
        <>
          <Heading4 className={styles.title}>Your new address</Heading4>
          <Heading4>{address}</Heading4>
        </>
      ) : null}

      <Heading4 className={styles.title}>Your moving day</Heading4>
      <Heading4>{movingDateText}</Heading4>

      <div className={styles.info}>
        We'll end your contract on this date, create a final bill and refund any
        money left on your account. Need to change the date? If you're no longer
        moving, you can cancel your move.
      </div>

      {!isTakingOvo && (
        <InfoNotification
          id="not-powering-your-home"
          className={styles.notification}
          title="We're not powering your new home"
        >
          <Stack spaceBetween={[2, 2, 2]}>
            <div>
              You're not taking OVO with you to your new home. You'll lose
              access to your perks like Beyond, banked EV miles and solar panels
              at the end of your contract.
            </div>
            <div>
              If you change your mind, you can return to this page at any time
              to find an energy tariff that works better for you.
            </div>
          </Stack>
        </InfoNotification>
      )}

      {showComeBackLaterCard && (
        <InfoNotification
          id="move-out-date-more-than-seven-days"
          data-testid="move-out-date-more-than-seven-days"
          className={styles.notification}
          title="Move date is more than 7 days in future"
        >
          <Stack spaceBetween={[2, 2, 2]}>
            <div>Please come back later.</div>
          </Stack>
        </InfoNotification>
      )}

      <Stack spaceBetween={5} className={styles.buttonsStack}>
        {!isMovingDateInPast ? (
          <div className={styles.buttonWrapper}>
            <AnalyticsClick
              name={HOME_MOVES_CHANGE_MOVING_DATE}
              properties={{
                currentMovingDate: movingDateText,
                isTakingOvo,
              }}
            >
              <SecondaryCTAButton
                className={styles.button}
                onClick={() => setShowChangeMovingDateModal(true)}
              >
                Change moving date
              </SecondaryCTAButton>
            </AnalyticsClick>
          </div>
        ) : null}
        <div className={styles.buttonWrapper}>
          <AnalyticsClick
            name={HOME_MOVES_CANCEL_MOVE}
            properties={{
              currentMovingDate: movingDateText,
              isTakingOvo,
            }}
          >
            <SecondaryCTAButton
              className={styles.button}
              onClick={() => setShowCancelMoveModal(true)}
            >
              Cancel my move
            </SecondaryCTAButton>
          </AnalyticsClick>
        </div>
      </Stack>
    </Card>
  );
};
